import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";

import { BarChart } from "src/components/charts/bar_chart";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { constructTitle } from "src/pages/reports/construct_notes";
import { DomainsCircle } from "src/pages/reports/fsci/v2/graphics/domains_circle";
import { FSCIBrandedV2Report } from "src/pages/reports/fsci/v2/types";
import { isNotNil } from "src/utils/type_guards";

import { meta } from "./meta";

/*
 * Types
 */

export type WellBeingProps = {
    readonly report: FSCIBrandedV2Report;
};

/*
 * Main
 */

export const WellBeing = (props: WellBeingProps): React.ReactElement => {
    const { report } = props;

    return (
        <div className="main-report-content-wrap">
            <div className="single-domain-result-top-wrap">
                <DomainsCircle activeDomain="wellBeing" inline />

                <PageTitle>Well-Being</PageTitle>
                <Body>
                    Well-being is an important domain for both student and educator flourishing. The FSCI identified
                    three constructs related to well-being—one for teachers and leaders (Stress), and two for students
                    (Healthy Living and Resilience). This domain and related constructs demonstrate that the well-being
                    of educators and students is not a secondary concern—but an essential contributor to flourishing
                    outcomes.
                </Body>
                <Body>
                    <em>
                        Construct and benchmark scores are presented as raw scores converted to a 100-point scale (a
                        common industry practice called criterion referencing). These scores give the reader some sense
                        of how strongly respondents agreed with the items within a construct (school score), which is
                        presented alongside the average respondent score in the normative sample (benchmark score).
                        Benchmark data is not included for the purpose of scoring or ranking your school against other
                        schools; rather, it is provided to help your school a) gauge priorities and b) track change over
                        subsequent administrations of the FSCI at your school.
                    </em>
                </Body>
                <Separator />
            </div>
            {pipe(
                R.keys(report.value.results.raw.wellBeing),
                A.map((construct) => (
                    <div className="an-opportunity-item domain-result" key={construct}>
                        {pipe(
                            R.lookup(construct, meta),
                            O.map((m) => (
                                <>
                                    <SectionTitle>{constructTitle(m)}</SectionTitle>
                                    <Body>{m.prologue}</Body>
                                    <BarChart
                                        data={[
                                            {
                                                label: report.value.year,
                                                value: report.value.results.raw.wellBeing[construct] * 100,
                                            },
                                            {
                                                label: "FSCI Benchmark",
                                                value: report.value.results.benchmarks.wellBeing[construct] * 100,
                                            },
                                        ]}
                                    />
                                    {isNotNil(m.extra) && <Body className="small-domain-text">{m.extra}</Body>}
                                </>
                            )),
                            O.getOrElse(() => <></>),
                        )}
                    </div>
                )),
            )}
        </div>
    );
};
